/**
 * Design System Component Exports
 * 
 * This file re-exports all UI components with design system adaptations.
 * Import components from here instead of directly from shadcn to ensure
 * design system consistency.
 * 
 * NOTE: You can now use path aliases to import design tokens directly:
 * - @ds/tokens - Design tokens (colors, spacing, etc.)
 * - @ds/themes - Theme definitions
 * - @ds/types - Shared interfaces
 * - @ds/utils - Utilities
 */

// Import shared design tokens
import { colors, spacing, typography } from '@repo/design-system/tokens';

// Re-export adapted components
export { Button } from '../adapters/Button';
export { 
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent
} from '../adapters/Card';
export { IconContainer } from '../adapters/IconContainer';
export { Input } from '../adapters/Input';
export { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../adapters/Select';
export { FormField } from '../adapters/FormField';
export { 
  Text,
  Heading
} from '../adapters/Text';
export { ThemeSwitcher } from '../adapters/ThemeSwitcher';
export { ThemeProvider, useTheme } from 'next-themes';
export { Footer } from '../../footer/Footer';
export { default as LiveChat } from '../adapters/LiveChat';

// Re-export components that don't have adapters yet
// These will be adapted in the future
export { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from '../avatar';
export { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../tabs';
export { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '../alert-dialog';
export { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../dropdown-menu';
export { Sonner, toast } from '../adapters/Sonner';
export { Checkbox } from '../checkbox';
export { Switch } from '../switch';
export { 
  Alert, 
  AlertTitle, 
  AlertDescription 
} from '../alert';
export { Label } from '../label';

// Define web-specific semantic tokens that map to the shared design tokens
export const semanticColors = {
  text: {
    primary: {
      light: 'rgb(17, 24, 39)', // gray-900
      dark: 'rgb(243, 244, 246)' // gray-100
    },
    secondary: {
      light: 'rgb(75, 85, 99)', // gray-600
      dark: 'rgb(209, 213, 219)' // gray-300
    },
    tertiary: {
      light: 'rgb(107, 114, 128)', // gray-500
      dark: 'rgb(156, 163, 175)' // gray-400
    }
  },
  background: {
    primary: {
      light: 'rgb(255, 255, 255)', // white
      dark: 'rgb(17, 24, 39)' // gray-900
    },
    secondary: {
      light: 'rgb(249, 250, 251)', // gray-50
      dark: 'rgb(31, 41, 55)' // gray-800
    }
  },
  border: {
    default: {
      light: 'rgb(229, 231, 235)', // gray-200
      dark: 'rgb(75, 85, 99)' // gray-600
    }
  },
  // Use the primary colors from the shared design tokens
  primary: {
    50: '#fef2f2',
    100: '#fee2e2',
    // ... add other color definitions
  }
};

// Map semantic spacing to shared design tokens 
export const semanticSpacing = {
  xs: 8, // 8px
  sm: 12, // 12px
  md: 16, // 16px
  lg: 24, // 24px
  xl: 32, // 32px
  '2xl': 48 // 48px
};

// Map semantic typography to shared design tokens
export const semanticTypography = {
  body: {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  },
  heading: {
    h1: 'text-4xl font-bold',
    h2: 'text-3xl font-semibold',
    h3: 'text-2xl font-semibold',
    h4: 'text-xl font-semibold',
    h5: 'text-lg font-medium',
    h6: 'text-base font-medium'
  },
  fontSize: {
    sm: '0.875rem',
    base: '1rem',
    // ... other font sizes
  }
};

// Define base tokens
export const baseColors = semanticColors;
export const baseSpacing = semanticSpacing;
export const baseTypography = semanticTypography;

// Re-export shared design tokens for direct use
export { colors, spacing, typography };

// Export the real ProductViewer with fallback support
export { ProductViewer } from '../adapters/ProductViewer';

/**
 * Usage:
 * 
 * ```tsx
 * // Instead of this:
 * import { Button } from '@/components/ui/button'
 * 
 * // Do this:
 * import { Button } from '@/components/ui/design-system'
 * ```
 */ 